import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  activateNewsBundle,
  fetchNewsDetail,
  fetchNewsDownloadProgress,
  fetchNewsList,
  fetchNewsPreview,
  fetchNewsPublicationStatus,
  rollbackNewsBundle,
  stageLatestNewsBundle,
} from "./api";
import type { NewsListFilters } from "./api";

export const newsStatusQueryKey = ["swing-trading", "news", "publication-status"] as const;
export const newsListQueryKey = ["swing-trading", "news", "list"] as const;

export function useNewsPublicationStatusQuery() {
  return useQuery({ queryKey: newsStatusQueryKey, queryFn: fetchNewsPublicationStatus, retry: false });
}

export function useNewsDownloadProgressQuery(watch: boolean) {
  return useQuery({
    queryKey: ["swing-trading", "news", "download-progress"],
    queryFn: fetchNewsDownloadProgress,
    retry: false,
    refetchInterval: watch ? 500 : false,
  });
}

export function useNewsListQuery(filters: NewsListFilters) {
  return useQuery({ queryKey: [...newsListQueryKey, filters], queryFn: () => fetchNewsList(filters), retry: false });
}

export function useNewsDetailQuery(newsID: string) {
  return useQuery({
    queryKey: ["swing-trading", "news", "detail", newsID],
    queryFn: () => fetchNewsDetail(newsID),
    enabled: newsID !== "",
    retry: false,
  });
}

export function useNewsPreviewQuery(bundleVersion: string) {
  return useQuery({
    queryKey: ["swing-trading", "news", "preview", bundleVersion],
    queryFn: () => fetchNewsPreview(bundleVersion),
    enabled: bundleVersion !== "",
    retry: false,
  });
}

function useRefreshNewsQueries() {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["swing-trading", "news"] });
  };
}

export function useStageNewsMutation() {
  const refresh = useRefreshNewsQueries();
  return useMutation({ mutationFn: stageLatestNewsBundle, onSettled: refresh });
}

export function useActivateNewsMutation() {
  const refresh = useRefreshNewsQueries();
  return useMutation({ mutationFn: activateNewsBundle, onSettled: refresh });
}

export function useRollbackNewsMutation() {
  const refresh = useRefreshNewsQueries();
  return useMutation({ mutationFn: rollbackNewsBundle, onSettled: refresh });
}
